/**
 * Workflows — ONE table per audience concern: "Your workflows" is the
 * tenant's own composed rows; "Personal" is what people set up for themselves in chat
 * (watches, report schedules), which admins can pause but not change; "Run by SC" (system rows) appears only when
 * the server ships them, which it does for SUPER_ADMIN alone — a tenant
 * admin composes on EVENTS, never on the platform's machinery. The footer
 * row is the unmatched-events counter — types nothing enabled listens to,
 * never dropped.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  IconButton,
  Modal,
  Select,
  Skeleton,
  Table,
} from '@servicecompressionllc/sc-design-system';
import { Trash2 } from '@servicecompressionllc/sc-design-system/icons';
import { AppShell } from '../../components/AppShell.js';
import { Toggle } from '../../components/Toggle.js';
import { formatDay } from '../../lib/format.js';
import type { BuilderWorkflow, WorkflowTrigger } from '../../lib/types.js';
import { SummarySentence } from './SummarySentence.js';
import { summarize } from './summarize.js';
import { useBuilderListLogic } from './useBuilderLogic.js';

/** Only the states the toggle CAN'T say. "Enabled" as a badge next to an
 *  enabled toggle was the same fact twice, wearing different clothes. */
const stateBadge = (w: BuilderWorkflow) => {
  if (w.enabled) return null;
  // An unfinished draft says so, and says how much is left — the enable gate
  // knows this already, so the list shouldn't make you discover it by trying.
  const missing = w.issues?.length ?? 0;
  if (missing > 0)
    return (
      <Badge variant="warning">
        {missing} thing{missing === 1 ? '' : 's'} missing
      </Badge>
    );
  if (w.kind === 'composed' && w.eventsLast7d === 0) return <Badge variant="neutral">Draft</Badge>;
  return <Badge variant="neutral">Paused</Badge>;
};

/** Engine words never reach the page — the same translation the runs page does. */
const RUN_WORDS: Record<string, { label: string; tone: string }> = {
  done: { label: 'finished', tone: 'text-success' },
  running: { label: 'running', tone: 'text-info' },
  suspended: { label: 'waiting on someone', tone: 'text-warning' },
  deferred: { label: 'paused — daily limit', tone: 'text-info' },
  failed: { label: "couldn't finish", tone: 'text-danger' },
};

const LastRun = ({ run }: { run?: { status: string; at: string } | null }) => {
  if (!run) return <span className="text-xs text-ink-subtle">Never run</span>;
  const words = RUN_WORDS[run.status] ?? { label: run.status, tone: 'text-ink-muted' };
  return (
    <span className="whitespace-nowrap text-ink-muted">
      {new Date(run.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
      <span className={`block text-xs ${words.tone}`}>{words.label}</span>
    </span>
  );
};

const WorkflowTable = ({
  workflows,
  toggleEnabled,
  onDelete,
  footer,
  triggers,
}: {
  workflows: BuilderWorkflow[];
  toggleEnabled: (id: string) => void;
  /** Composed rows only — system workflows aren't deletable from the UI. */
  onDelete?: (w: BuilderWorkflow) => void;
  footer?: React.ReactNode;
  triggers: WorkflowTrigger[];
}) => (
  <div className="overflow-x-auto rounded-lg border border-outline">
    <Table.Root className="w-full min-w-[48rem] text-sm">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Workflow</Table.HeaderCell>
          <Table.HeaderCell>Last run</Table.HeaderCell>
          <Table.HeaderCell>Enabled</Table.HeaderCell>
          {onDelete && <Table.HeaderCell aria-label="Actions" />}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {workflows.map((w) => (
          <Table.Row key={w.id}>
            <Table.Cell>
              <span className="flex items-center gap-2">
                <Link
                  to={`/settings/workflows/${w.id}`}
                  className="font-medium text-primary hover:underline"
                >
                  {w.name}
                </Link>
                {stateBadge(w)}
              </span>
              {/* System rows keep their steps server-side, so only a composed
                  definition can be read out; the rest show their name alone.
                  Clamped: one workflow's ten-line prose was shoving every
                  other column into a cramped island — the detail page owns
                  the full sentence. */}
              {w.definition && (
                <SummarySentence
                  className="mt-0.5 line-clamp-2 max-w-2xl text-xs leading-5 text-ink-muted"
                  tokens={summarize(w.trigger, w.definition, { triggers, schedule: w.schedule })}
                />
              )}
            </Table.Cell>
            <Table.Cell>
              <LastRun run={w.lastRun} />
            </Table.Cell>
            <Table.Cell>
              <Toggle
                checked={w.enabled}
                onChange={() => toggleEnabled(w.id)}
                label={`${w.name} enabled`}
              />
            </Table.Cell>
            {onDelete && (
              <Table.Cell align="right">
                <IconButton
                  aria-label={`Delete ${w.name}`}
                  size="sm"
                  variant="ghost"
                  onClick={() => onDelete(w)}
                >
                  <Trash2 />
                </IconButton>
              </Table.Cell>
            )}
          </Table.Row>
        ))}
        {workflows.length === 0 && (
          <Table.Row>
            <Table.Cell colSpan={onDelete ? 4 : 3} className="text-sm text-ink-muted">
              Nothing here yet — build one and it runs whenever its event arrives.
            </Table.Cell>
          </Table.Row>
        )}
        {footer}
      </Table.Body>
    </Table.Root>
  </div>
);

const hasEnded = (w: BuilderWorkflow, now: Date): boolean =>
  w.endsAt != null && new Date(w.endsAt) <= now;

/** Which personal workflows the tab lists: still going by default, so ended ones don't pile up. */
type PersonalStatus = 'active' | 'ended' | 'all';
const PERSONAL_STATUS_OPTIONS: { value: PersonalStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'ended', label: 'Ended' },
  { value: 'all', label: 'All' },
];
const personalStatusOf = (raw: string | null): PersonalStatus =>
  raw === 'ended' || raw === 'all' ? raw : 'active';

/** What people set up for themselves. Admins see who it belongs to and can pause it; the
 *  owner is the only one who changes or deletes it, so there's no link and no delete. */
const PersonalTable = ({
  workflows,
  toggleEnabled,
  triggers,
}: {
  workflows: BuilderWorkflow[];
  toggleEnabled: (id: string) => void;
  triggers: WorkflowTrigger[];
}) => {
  const now = new Date();
  return (
    <div className="overflow-x-auto rounded-lg border border-outline">
      <Table.Root className="w-full min-w-[48rem] text-sm">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Workflow</Table.HeaderCell>
            <Table.HeaderCell>Belongs to</Table.HeaderCell>
            <Table.HeaderCell>Ends</Table.HeaderCell>
            <Table.HeaderCell>Last run</Table.HeaderCell>
            <Table.HeaderCell>Running</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {workflows.map((w) => {
            const ended = hasEnded(w, now);
            return (
              <Table.Row key={w.id}>
                <Table.Cell>
                  <span className="font-medium text-ink">{w.name}</span>
                  {w.definition && (
                    <SummarySentence
                      className="mt-0.5 line-clamp-2 max-w-2xl text-xs leading-5 text-ink-muted"
                      tokens={summarize(w.trigger, w.definition, {
                        triggers,
                        schedule: w.schedule,
                      })}
                    />
                  )}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">{w.owner?.name}</Table.Cell>
                <Table.Cell className="whitespace-nowrap text-ink-muted">
                  {w.endsAt ? `${ended ? 'Ended ' : ''}${formatDay(w.endsAt)}` : 'No end'}
                </Table.Cell>
                <Table.Cell>
                  <LastRun run={w.lastRun} />
                </Table.Cell>
                <Table.Cell>
                  {w.brokenReason ? (
                    // The reason is for whoever asks; its owner's bot already told them.
                    <span className="text-ink-subtle" title={w.brokenReason}>
                      Broken
                    </span>
                  ) : ended ? (
                    <span className="text-ink-subtle">Ended</span>
                  ) : (
                    <Toggle
                      checked={w.enabled}
                      onChange={() => toggleEnabled(w.id)}
                      label={`${w.name} running`}
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
          {workflows.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={5} className="text-sm text-ink-muted">
                Nothing for this person.
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </div>
  );
};

type Tab = 'mine' | 'personal' | 'builtIn';
const TAB_PARAM: Record<Tab, string | null> = { mine: null, personal: 'personal', builtIn: 'sc' };

export const BuilderListPage = () => {
  const navigate = useNavigate();
  const {
    workflows,
    unmatched,
    toggleEnabled,
    deleteWorkflow,
    deleting,
    deleteError,
    triggers,
    activity,
    isLoading,
    error,
  } = useBuilderListLogic();
  const mine = workflows.filter((w) => w.kind === 'composed' && !w.owner);
  const personal = workflows.filter((w) => w.kind === 'composed' && w.owner);
  const builtIn = workflows.filter((w) => w.kind === 'system');
  // The tab and the person filter live in the URL, so a filtered list is a link.
  // Set-and-forget machinery hides behind its own tab — it's noise beside
  // the workflows someone actually manages. Only rendered when the server
  // ships system rows (SUPER_ADMIN).
  const [params, setParams] = useSearchParams();
  const tab: Tab =
    params.get('tab') === 'personal' ? 'personal' : params.get('tab') === 'sc' ? 'builtIn' : 'mine';
  const ownerId = params.get('owner');
  const status = personalStatusOf(params.get('status'));
  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    const value = TAB_PARAM[next];
    if (value) p.set('tab', value);
    else p.delete('tab');
    if (next !== 'personal') {
      p.delete('owner');
      p.delete('status');
    }
    setParams(p);
  };
  const setOwner = (id: string) => {
    const p = new URLSearchParams(params);
    if (id) p.set('owner', id);
    else p.delete('owner');
    setParams(p);
  };
  const setStatus = (next: PersonalStatus) => {
    const p = new URLSearchParams(params);
    if (next === 'active') p.delete('status');
    else p.set('status', next);
    setParams(p);
  };
  const owners = [
    ...new Map(personal.flatMap((w) => (w.owner ? [[w.owner.id, w.owner.name] as const] : []))),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const now = new Date();
  const activePersonal = personal.filter((w) => !hasEnded(w, now));
  const shownPersonal = personal
    .filter((w) => !ownerId || w.owner?.id === ownerId)
    .filter((w) => status === 'all' || hasEnded(w, now) === (status === 'ended'));
  const tabs = [
    { id: 'mine' as const, label: 'Your workflows' },
    ...(personal.length > 0
      ? [{ id: 'personal' as const, label: 'Personal', count: activePersonal.length }]
      : []),
    ...(builtIn.length > 0 ? [{ id: 'builtIn' as const, label: 'Run by SC' }] : []),
  ];
  const [confirmDelete, setConfirmDelete] = useState<BuilderWorkflow | null>(null);

  return (
    <AppShell>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Workflows</h1>
          <p className="text-sm text-ink-muted">
            Things that happen on their own, written the way you&apos;d say them out loud.
          </p>
        </div>
        <div className="flex flex-none items-center gap-2">
          <Button variant="secondary" asChild>
            <Link to="/settings/workflows/activity">Activity</Link>
          </Button>
          <Button onClick={() => navigate('/settings/workflows/templates')}>New workflow</Button>
        </div>
      </div>

      {error !== null && <Banner variant="danger">Couldn’t load workflows.</Banner>}

      {/* Runs that need a person come first — "did it work?" is a front-door
          question, not something to go looking for inside one workflow. */}
      {(activity.waiting > 0 || activity.failed > 0) && (
        <Banner variant={activity.failed > 0 ? 'danger' : 'warning'} className="mb-4">
          <span className="flex-1">
            {activity.waiting > 0 && (
              <>
                <span className="font-semibold">
                  {activity.waiting} run{activity.waiting === 1 ? '' : 's'}
                </span>{' '}
                {activity.waiting === 1 ? 'is' : 'are'} waiting on a person
              </>
            )}
            {activity.waiting > 0 && activity.failed > 0 && ' · '}
            {activity.failed > 0 && (
              <>
                <span className="font-semibold">
                  {activity.failed} run{activity.failed === 1 ? '' : 's'}
                </span>{' '}
                couldn’t finish in the last 7 days (workflow runs — the Events page counts events, a
                different unit)
              </>
            )}
          </span>
          <Link
            to={`/settings/workflows/activity${activity.failed > 0 ? '?status=failed' : '?status=suspended'}`}
            className="flex-none font-medium"
          >
            Open activity
          </Link>
        </Banner>
      )}
      {/* The best funnel on the page — events arriving that nothing reacts
          to — used to hide in a table footer. It IS the reason to build the
          next workflow, so it sits beside the button that builds one. */}
      {unmatched.count > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-warning-border bg-warning-soft px-4 py-3 text-sm">
          <span className="font-semibold text-warning">
            {unmatched.count} things happened in the last 7 days that no workflow was listening for.
          </span>
          <Link to="/events?view=all&unrouted=true" className="text-primary hover:underline">
            See what they were
          </Link>
          <span className="text-ink-muted">·</span>
          <Link to="/settings/workflows/templates" className="text-primary hover:underline">
            Start a workflow
          </Link>
        </div>
      )}
      {tabs.length > 1 && (
        <div className="mb-4 flex gap-1 border-b border-outline" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'border-primary text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {t.label}
              {'count' in t && <Badge variant="neutral">{t.count}</Badge>}
            </button>
          ))}
        </div>
      )}
      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : tab === 'personal' && personal.length > 0 ? (
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-ink-muted">
              Belongs to
              <span className="w-56">
                <Select
                  aria-label="Belongs to"
                  options={[
                    { value: '', label: 'Everyone' },
                    ...owners.map(([id, name]) => ({ value: id, label: name })),
                  ]}
                  value={[ownerId ?? '']}
                  onChange={(v) => setOwner(v[0] ?? '')}
                />
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-muted">
              Status
              <span className="w-40">
                <Select
                  aria-label="Status"
                  options={PERSONAL_STATUS_OPTIONS}
                  value={[status]}
                  onChange={(v) => setStatus(personalStatusOf(v[0] ?? null))}
                />
              </span>
            </label>
          </div>
          {shownPersonal.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {status === 'ended' ? 'Nothing has ended yet.' : 'Nothing is running right now.'}
            </p>
          ) : (
            <PersonalTable
              workflows={shownPersonal}
              toggleEnabled={toggleEnabled}
              triggers={triggers}
            />
          )}
        </div>
      ) : tab === 'builtIn' && builtIn.length > 0 ? (
        <div>
          <p className="mb-2 text-xs text-ink-muted">
            Platform machinery SC builds and maintains — visible to SC operators only.
          </p>
          <WorkflowTable workflows={builtIn} toggleEnabled={toggleEnabled} triggers={triggers} />
        </div>
      ) : (
        <div className="grid gap-6">
          <WorkflowTable
            workflows={mine}
            toggleEnabled={toggleEnabled}
            onDelete={setConfirmDelete}
            triggers={triggers}
          />
        </div>
      )}

      <Modal open={confirmDelete !== null} onOpenChange={() => setConfirmDelete(null)}>
        <Modal.Header>
          <Modal.Title>Delete workflow</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-ink-muted">
            Delete &ldquo;{confirmDelete?.name}&rdquo;?
            {confirmDelete?.enabled && ' It will be turned off first.'} It stops reacting to{' '}
            <code className="text-xs">{confirmDelete?.trigger}</code> permanently — runs already
            recorded keep their history. This can&apos;t be undone.
          </p>
          {deleteError !== null && (
            <p className="mt-2 text-xs font-semibold text-danger">
              {deleteError instanceof Error ? deleteError.message : 'Delete failed.'}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={deleting}
            onClick={() => {
              if (!confirmDelete) return;
              // Close on success only — a failure (e.g. runs still waiting on
              // someone) stays visible in the modal.
              void deleteWorkflow(confirmDelete.id)
                .then(() => setConfirmDelete(null))
                .catch(() => undefined);
            }}
          >
            {deleting ? 'Deleting…' : 'Delete workflow'}
          </Button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
};
